import { useState } from 'react';
import { Button, Container, Paper, Title, TextInput, PasswordInput, Alert, Stack, Text, Anchor } from '@mantine/core';
import { IconUser, IconLock, IconBrandGoogle } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';
import config from 'config/config';
import { generatePKCEParams } from 'utils/pkce';

const getKeycloakRegistrationUrl = async (): Promise<string | null> => {
    if (!config.KEYCLOAK_URL || !config.KEYCLOAK_REALM || !config.KEYCLOAK_CLIENT_ID) {
        return null;
    }
    const baseUrl = config.KEYCLOAK_URL.replace(/\/$/, '');
    const realm = config.KEYCLOAK_REALM;
    const clientId = config.KEYCLOAK_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);

    // Generate PKCE parameters
    const { codeChallenge } = await generatePKCEParams();

    // Use Keycloak's authorization endpoint with registration action and PKCE
    return `${baseUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=S256&kc_action=REGISTER`;
};

const EmbeddedLogin: React.FC = () => {
    const { loginWithPassword, loginWithGoogle, isLoading, error, hasConfig } = useAuth();
    const [credentials, setCredentials] = useState({ username: '', password: '' });

    const handleChange = (field: 'username' | 'password') => (event: React.ChangeEvent<HTMLInputElement>) => {
        setCredentials(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSubmit = async () => {
        if (!credentials.username || !credentials.password) {
            return;
        }
        await loginWithPassword(credentials.username, credentials.password);
    };

    return (
        <Container className="embedded-login">
            <Paper p="xl" withBorder>
                <Title order={2} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconUser size={24} />
                    Tammilanit Intra
                </Title>
                {!hasConfig && (
                    <Alert color="yellow" mb="md">
                        Keycloak-yhteyden asetuksia ei ole vielä konfiguroitu. Lisää <code>VITE_KEYCLOAK_URL</code>
                        , <code>VITE_KEYCLOAK_REALM</code>, <code>VITE_KEYCLOAK_CLIENT_ID</code> ja
                        tarvittaessa <code>VITE_KEYCLOAK_AUDIENCE</code> buildiin kytkeäksesi kirjautumisen.
                    </Alert>
                )}
                {error && <Alert color="red" mb="md">{error}</Alert>}
                {hasConfig && (
                    <>
                        {/* Extracted login option components for easier re-organization */}

                        {/*
                            Default login options order. 
                            In the future, this can come from SettingsContext, e.g.:
                            const loginOptionOrder = useContext(SettingsContext).loginOptionOrder ?? defaultLoginOptionOrder;
                        */}
                        {(() => {
                            const loginOptions = [
                                'password',
                                'google'
                            ];

                            // Map option keys to their components
                            const optionComponents = {
                                google: (
                                    <div key="google-option" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                                        <Button
                                            color="red"
                                            leftSection={<IconBrandGoogle size={20} />}
                                            fullWidth
                                            size="lg"
                                            onClick={loginWithGoogle}
                                            disabled={isLoading}
                                            loading={isLoading}
                                        >
                                            Kirjaudu Google-tilillä
                                        </Button>
                                    </div>
                                ),
                                password: (
                                    <Stack key="password-option" gap="md">
                                        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} autoComplete="off">
                                            <Stack gap="md">
                                                <TextInput
                                                    label="Käyttäjätunnus"
                                                    name="username"
                                                    value={credentials.username}
                                                    onChange={handleChange('username')}
                                                    required
                                                    disabled={!hasConfig || isLoading}
                                                />
                                                <PasswordInput
                                                    label="Salasana"
                                                    name="password"
                                                    value={credentials.password}
                                                    onChange={handleChange('password')}
                                                    required
                                                    disabled={!hasConfig || isLoading}
                                                />
                                                <Button
                                                    type="submit"
                                                    variant="filled"
                                                    fullWidth
                                                    size="lg"
                                                    loading={isLoading}
                                                    disabled={!hasConfig || !credentials.username || !credentials.password}
                                                    leftSection={<IconLock size={20} />}
                                                >
                                                    Kirjaudu sisään
                                                </Button>
                                            </Stack>
                                        </form>
                                    </Stack>
                                ),
                            };

                            // Build array with divider between options if more than one
                            const renderedOptions: React.ReactNode[] = [];
                            loginOptions.forEach((option, idx) => {
                                if (idx > 0) {
                                    // Insert divider between options
                                    renderedOptions.push(
                                        <div
                                            key={`divider-${idx}`}
                                            style={{
                                                marginTop: '1rem',
                                                marginBottom: '1rem',
                                                textAlign: 'center',
                                                position: 'relative'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: '50%',
                                                    top: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    background: 'white',
                                                    padding: '0 1rem'
                                                }}
                                            >
                                                tai
                                            </div>
                                            <div style={{ borderTop: '1px solid #ddd' }}></div>
                                        </div>
                                    );
                                }
                                const optionKey = option as keyof typeof optionComponents;
                                if (optionComponents[optionKey]) {
                                    renderedOptions.push(optionComponents[optionKey]);
                                }
                            });
                            return renderedOptions;
                        })()}
                        
                        <Alert color="blue" mt="md" style={{ textAlign: 'center' }}>
                            <Text>
                                Ei käyttäjätiliä?{' '}
                                <Anchor
                                    href="#"
                                    onClick={async e => {
                                        e.preventDefault();
                                        const url = await getKeycloakRegistrationUrl();
                                        if (url) {
                                            window.open(url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Rekisteröidy tästä
                                </Anchor>
                            </Text>
                        </Alert>
                    </>
                )}

            </Paper>
        </Container>
    );
};

export default EmbeddedLogin;
